/**
 * OpsRetrievalPreviewPanel
 *
 * Preview results panel for the OPS retrieval modal (Panel 2).
 * Shows patent count, duration estimates, CQL, sample results,
 * KB destination selector, optional download-limit input, and
 * the two download action buttons.
 * Stateless except for the download-limit value, which is local.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDuration, type OpsPreviewResult } from '../services/opsRetrievalService';
import type { KnowledgeBase } from '../services/knowledgeBaseService';
import { TextInput } from './OpsRetrievalFormParts';

export interface OpsRetrievalPreviewPanelProps {
  previewResult: OpsPreviewResult;
  samplesLoading?: boolean;
  samplesError?: string | null;
  kbs: KnowledgeBase[];
  kbsLoading: boolean;
  selectedKbId: string;
  setSelectedKbId: (id: string) => void;
  newKbName: string;
  setNewKbName: (name: string) => void;
  kbReady: boolean;
  launching: boolean;
  launchError: string | null;
  onModify: () => void;
  /** Called with the selected mode AND the effective download limit (null = no limit). */
  onLaunch: (mode: 'list' | 'full', limit: number | null) => void;
}

export default function OpsRetrievalPreviewPanel({
  previewResult,
  samplesLoading = false,
  samplesError = null,
  kbs, kbsLoading, selectedKbId, setSelectedKbId, newKbName, setNewKbName, kbReady,
  launching, launchError,
  onModify, onLaunch,
}: OpsRetrievalPreviewPanelProps) {
  const { t } = useTranslation();
  const {
    total_count, effective_count, sample, cql, term_count, warnings,
    estimated_list_seconds, estimated_full_seconds, estimated_full_google_seconds,
  } = previewResult;

  // ── Download-limit state (panel-local) ───────────────────────────────────────
  const [limitInput, setLimitInput] = useState('');

  const maxAvailable = Math.min(effective_count, 2000);

  /** Parse the limit input; returns null when "no limit". */
  const parsedLimit = (): number | null => {
    const v = parseInt(limitInput, 10);
    if (isNaN(v) || v <= 0) return null;
    return Math.min(v, maxAvailable);
  };

  const effectiveLimit = parsedLimit();
  const displayCount = effectiveLimit ?? maxAvailable;

  /** Proportionally scale an estimated duration to the display count. */
  const scaleDuration = (baseSec: number): number =>
    maxAvailable > 0 ? Math.max(1, Math.round((displayCount / maxAvailable) * baseSec)) : baseSec;

  const listSec  = scaleDuration(estimated_list_seconds);
  const fullSec  = scaleDuration(estimated_full_seconds);
  const googleSec = scaleDuration(estimated_full_google_seconds);

  return (
    <>
      <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4 text-sm">

        {/* Count + term badge */}
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-3xl font-bold text-slate-800">{total_count.toLocaleString()}</span>
          <span className="text-slate-500">{t('opsRetrieval.preview.patentsFound', 'patents found')}</span>
          <span className="text-xs text-slate-400 bg-slate-100 rounded px-2 py-0.5">
            {term_count} {t('opsRetrieval.preview.terms', 'terms')}
          </span>
          <span
            className="text-xs text-slate-400 italic"
            title={t('opsRetrieval.preview.countApproxTitle', 'OPS counts may include multiple editions of the same patent (A1/A2/B1…). The actual number downloaded may be lower.')}
          >
            {t('opsRetrieval.preview.countApprox', '(OPS estimate)')}
          </span>
        </div>

        {/* Warnings */}
        {warnings.map((w, i) => (
          <div key={i} className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">{w}</div>
        ))}

        {/* Duration estimates (2 columns) */}
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-slate-200 rounded p-3">
            <p className="text-xs text-slate-500 mb-1">{t('opsRetrieval.preview.listMode', 'Métadonnées (titre + résumé + CPC)')}</p>
            <p className="text-base font-semibold text-slate-700">{formatDuration(listSec)}</p>
            <p className="text-xs text-slate-400">{displayCount.toLocaleString()} {t('opsRetrieval.preview.patents', 'brevets')}</p>
          </div>
          <div className="border border-slate-200 rounded p-3">
            <p className="text-xs text-slate-500 mb-1">{t('opsRetrieval.preview.fullMode', 'Texte intégral (description + revendications)')}</p>
            <p className="text-base font-semibold text-green-700">~{formatDuration(googleSec)}</p>
            <p className="text-xs text-slate-400 mt-0.5">via Google Patents</p>
            {fullSec !== googleSec && (
              <p className="text-xs text-slate-400 mt-0.5">({t('opsRetrieval.preview.opsFallback', 'OPS fallback')}: ~{formatDuration(fullSec)})</p>
            )}
            <p className="text-xs text-slate-400">{displayCount.toLocaleString()} {t('opsRetrieval.preview.patents', 'brevets')}</p>
          </div>
        </div>

        {/* Download limit */}
        <div className="border border-slate-200 rounded p-3 bg-slate-50">
          <p className="text-xs font-semibold text-slate-600 mb-2">
            {t('opsRetrieval.preview.limitTitle', 'Download limit (optional)')}
          </p>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={1}
              max={maxAvailable}
              placeholder={t('opsRetrieval.preview.limitPlaceholder', 'e.g. 10  (blank = all {{n}})', { n: maxAvailable.toLocaleString() })}
              value={limitInput}
              onChange={(e) => setLimitInput(e.target.value)}
              className="w-48 border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
            />
            {effectiveLimit != null && (
              <span className="text-xs text-blue-700 font-medium">
                {t('opsRetrieval.preview.limitActive', 'Will download first {{n}} patents', { n: effectiveLimit.toLocaleString() })}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-1.5">
            {t('opsRetrieval.preview.limitHint', 'Leave blank to download all available patents (up to {{n}}).', { n: maxAvailable.toLocaleString() })}
          </p>
        </div>

        {/* CQL (collapsible) */}
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
            {t('opsRetrieval.preview.cqlLabel', 'CQL query')}
          </summary>
          <pre className="mt-1 p-2 bg-slate-50 border border-slate-200 rounded font-mono whitespace-pre-wrap break-all text-slate-600">{cql}</pre>
        </details>

        {/* Sample rows — progressive display */}
        {(sample.length > 0 || samplesLoading) && (
          <details className="text-xs" open>
            <summary className="cursor-pointer text-slate-500 hover:text-slate-700 mb-1">
              {t('opsRetrieval.preview.sampleLabel', 'Sample results (first 5)')}
              {samplesLoading && (
                <span className="ml-2 inline-flex items-center gap-1 text-slate-400">
                  <span className="w-2.5 h-2.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                  {t('opsRetrieval.preview.loadingSamples', 'Loading...')}
                </span>
              )}
            </summary>
            <div className="space-y-1.5 mt-1">
              {sample.map((s) => (
                <div key={s.document_id} className="border border-slate-200 rounded p-2 bg-white">
                  <p className="font-medium text-slate-700">{s.document_id}</p>
                  <p className="text-slate-600 truncate">{s.title}</p>
                  {(s.cpc_codes?.length ?? 0) > 0 && (
                    <p className="text-slate-400 mt-0.5">{s.cpc_codes?.join(' · ')}</p>
                  )}
                </div>
              ))}
            </div>
          </details>
        )}

        {samplesError && (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
            {t('opsRetrieval.preview.samplesError', 'Samples unavailable')}: {samplesError}
          </p>
        )}

        {/* KB destination */}
        <div className="border border-slate-200 rounded p-3 bg-slate-50">
          <p className="text-xs font-semibold text-slate-600 mb-2">
            {t('opsRetrieval.preview.kbDestination', 'Destination Knowledge Base')}
          </p>
          {kbsLoading ? (
            <p className="text-xs text-slate-400">{t('common.loading', 'Loading…')}</p>
          ) : (
            <>
              <select
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
                value={selectedKbId}
                onChange={(e) => {
                  setSelectedKbId(e.target.value);
                  if (e.target.value !== '__new__') setNewKbName('');
                }}
              >
                <option value="__new__">{t('opsRetrieval.preview.newKb', '+ New Knowledge Base…')}</option>
                {kbs.map((kb) => (
                  <option key={kb.id} value={kb.id}>{kb.name}</option>
                ))}
              </select>
              {selectedKbId === '__new__' && (
                <div className="mt-2">
                  <TextInput
                    placeholder={t('opsRetrieval.preview.newKbPlaceholder', 'Knowledge Base name…')}
                    value={newKbName}
                    onChange={(e) => setNewKbName(e.target.value)}
                    autoFocus
                  />
                </div>
              )}
            </>
          )}
        </div>

        {launchError && (
          <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{launchError}</p>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-200 flex-shrink-0">
        <button
          type="button"
          className="px-3 py-2 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200 flex-shrink-0"
          onClick={onModify}
        >
          {t('opsRetrieval.preview.modifyQuery', 'Modifier la requête')}
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            className="px-3 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
            disabled={launching || effective_count === 0 || !kbReady}
            onClick={() => onLaunch('list', effectiveLimit)}
            title={`~${formatDuration(listSec)}`}
          >
            {launching
              ? t('opsRetrieval.preview.launching', 'Démarrage…')
              : `${t('opsRetrieval.preview.downloadList', 'Télécharger les métadonnées')} (~${formatDuration(listSec)})`}
          </button>
          <button
            type="button"
            className="px-3 py-2 text-sm bg-slate-700 text-white rounded hover:bg-slate-800 disabled:opacity-50"
            disabled={launching || effective_count === 0 || !kbReady}
            onClick={() => onLaunch('full', effectiveLimit)}
            title={`~${formatDuration(googleSec)} via Google Patents`}
          >
            {launching
              ? t('opsRetrieval.preview.launching', 'Démarrage…')
              : `${t('opsRetrieval.preview.downloadFull', 'Télécharger le texte intégral')} (~${formatDuration(googleSec)})`}
          </button>
        </div>
      </div>
    </>
  );
}
